import URLSearchParams from '@ungap/url-search-params'
import { isTwaMode } from '../bridge_interface/bridge_interface' // Polyfill for < v49

import GA4Handler from '../ga4_support/GA4Handler'
import { DataLayerAppAdGridEvent, DataLayerOptionalEvent } from '../ga4_support/interfaces'
import affiliate_en from './affiliate/en'
import apps_en from './apps/en'
import {
  IAppAdGridBlockCache,
  IAdGridItem,
  TAppAdGridLanguage,
  IIgniteCampaign,
  IIgniteCampaignResponse
} from './interfaces'
import {
  TBlockAppAdGridSettingsFallbackBehavior,
  TBlockAppAdGridSettingsType
} from 'types-generator/page-configuration/IPageConfiguration'
import { androidAID, doNotTrack } from '../device'
import { drawStars } from '../utilities/drawStars'
import { getUserAgentUsingClientHints } from './ua-retrofill'
import { lazyload } from '../utilities/DOM_events'
import { neptuneDebug, neptuneWarn } from '../../../utilities'
import { shuffle } from '../utilities'
import { fetchWithOptions, FetchOptions } from '../utilities/net'

const CONTENT_ITEMS = {
  app: {
    en: apps_en
  },
  affiliate: {
    en: affiliate_en
  }
}

const fillAppAdElements = (elements: Element[], items: IAdGridItem[], type: string) => {
  elements.map((el, index) => {
    const app = items[index]

    const link: HTMLElement = el.querySelector('a.ad-grid')
    link.setAttribute('href', app.url)
    link.setAttribute('data-gtm-event-label', app.title)

    const image: HTMLElement = el.querySelector('.ad-grid__image')
    image.setAttribute('src', app.image)
    image.setAttribute('alt', app.title)

    const title: HTMLElement = el.querySelector('.ad-grid__title')
    title.innerHTML = app.title

    switch (type) {
      case 'app':
      case 'dynamic':
        const appStars = drawStars(app.starCount)
        const footer: HTMLElement = el.querySelector('.ad-grid__starCount')
        footer.innerHTML = appStars

        const optional = { title: app.title }

        link.addEventListener('click', e => {
          GA4Handler.clickHandleEvent(e, optional)
        })

        // Setup impression handling at link level.
        GA4Handler.impressionHandleEvent(link, optional)
        break
    }

    el.classList.remove('skeleton')
  })
}

const cacheHandler = async (config: AppAdGridBlockConfig) => {
  const lsKey = 'AppAdGridBlockCache'
  const cache: IAppAdGridBlockCache = JSON.parse(localStorage.getItem(lsKey) || '{}')
  const now = Date.now()
  const timeout = config.cacheTimeHours * 60 * 60 * 1000

  for (const id of Object.keys(cache)) {
    neptuneDebug(`${id} ${cache[id].timestamp} < ${now - timeout} (${now} - ${timeout})`)
    if (cache[id].timestamp < now - timeout) {
      neptuneDebug(`cacheRead: ${id}: clearing expired cache entry`)
      delete cache[id]
    }
  }

  // Cache miss, read from API.
  if (!cache[config.blockCacheId]) {
    neptuneDebug(`cacheRead: ${config.blockCacheId} CACHE MISS`)
    cache[config.blockCacheId] = {
      timestamp: now,
      items: await getItemsFromApi(config)
    }
  } else {
    neptuneDebug(`cacheRead: ${config.blockCacheId} CACHE HIT`)
  }

  // Store entire cache.
  localStorage.setItem(lsKey, JSON.stringify(cache))

  return cache[config.blockCacheId].items
}

const getUserAgentData = async () => {
  const w = window as any
  const hints = ['architecture', 'bitness', 'formFactor', 'fullVersionList', 'model', 'platformVersion', 'wow64']

  const agent = await getUserAgentUsingClientHints(hints)
  if (agent) {
    neptuneDebug('getUserAgentData: using hints: agent: ', agent)
    return agent
  }

  neptuneDebug('getUserAgentData: using default: agent: ', w.navigator.userAgent)

  return w.navigator.userAgent
}

interface GetAdsParams {
  aaid: string
  acceptLanguageHeader: TAppAdGridLanguage

  /**
   * Probably doesn't do anything but no harm in including
   */
  carrierKind: 'production'
  clickProxy: 'deeplink'
  countryCode?: string // US.

  /**
   * Probably doesn't do anything but no harm in including
   */
  deviceSpec: 'test'

  /**
   * Needed to apply ad server algo
   */
  flows: 'widget'

  includeDetails: true
  partner: string // essentials
  placementId: 1
  randomization: boolean // true
  sessionId: number
  siteId: number
  totalCampaignsRequested: number
  userAgentHeader: string
}

/**
 * Creates an ignite campaign API (getAds) URL.
 *
 * Previous query string values from examples:
 *   id: 5351,
 *   password: 'YUA9UXAQG7NEQQDIBKSHFHVERM',
 *   userAgentHeader: 'Dalvik%2F2.1.0+%28Linux%3B+U%3B+Android+14%3B+SM-S918U+Build%2FUP1A.230623.007%29',
 *
 * Previous API proxies used for testing:
 *  http://widget.markburgess.net.global.prod.fastly.net/web/getAds?
 *  https://fotoscapes-player-test-b.devsandbox.mobi/v2/getAds?
 *  https://api.markburgess.net/web/getAds?
 */
export const getUrl = async (config: AppAdGridBlockConfig) => {
  // noinspection SpellCheckingInspection
  const params: GetAdsParams = {
    aaid: androidAID(),
    acceptLanguageHeader: config.language,
    carrierKind: 'production',
    clickProxy: 'deeplink',
    deviceSpec: 'test',
    flows: 'widget',
    includeDetails: true,
    partner: config.partner,
    placementId: 1,
    randomization: true,
    sessionId: Date.now(),
    siteId: config.siteId,
    // Note, the plan is to change this to 25 to match the widget, but want to verify with Ali first,
    // using different counts on blocks using the global cache can result in an error depending on
    // which block is viewed first.
    totalCampaignsRequested: 12, // config.count,
    userAgentHeader: encodeURIComponent(await getUserAgentData())
  }

  // We are going to use the country code from geolocation in the CDN proxy by default.
  if (config.countryCodeFromPlatform) {
    params.countryCode = config.countryCode
  }

  const queryValues = Object.entries(params).reduce((acc: any, [key, value]) => {
    acc.push(`${key}=${value}`)
    return acc
  }, [])

  neptuneDebug('queryValues: ', queryValues)

  const domain =
    window.mp_globals.env.environment === 'live' ? 'ads.cm.digitalturbine.com' : 'ads.sandbox.cm.digitalturbine.com'

  return `https://${domain}/web/getAds?${queryValues.join('&')}`
}

const getItemsFromApi = async (config: AppAdGridBlockConfig) => {
  const url = await getUrl(config)
  const response = await fetchWithOptions({
    url,
    headers: {
      Accept: 'application/json'
    }
  } as FetchOptions)

  const data = response.data as IIgniteCampaignResponse

  // Force error for QA testing.
  const params = new URLSearchParams(window?.location?.search)
  if (params.has('neptune_app_ad_grid_block_qa_no_campaigns')) {
    neptuneDebug('FOUND neptune_app_ad_grid_block_qa_no_campaigns, forcing empty campaigns error')
    data.campaigns = []
  }

  if (!data.campaigns || !data.campaigns.length) {
    throw new Error(`getItemsFromApi failed: no campaigns returned from API for url ${url}`)
  }

  return data.campaigns.map((campaign: IIgniteCampaign): IAdGridItem => {
    return {
      title: campaign.productName,
      url: campaign.clickProxyURL,
      starCount: campaign.rating,
      image: campaign.productThumbnail
    }
  })
}

const getItems = async (config: AppAdGridBlockConfig) => {
  // Collapsing without android ID is handled in initAppAdBlocks.
  if (config.isDynamic && config.androidAID) {
    try {
      return await cacheHandler(config)
    } catch (e) {
      if (config.fallbackBehavior === TBlockAppAdGridSettingsFallbackBehavior.static) {
        return CONTENT_ITEMS[TBlockAppAdGridSettingsType.app][config.language]
      } else {
        throw e
      }
    }
  }

  return CONTENT_ITEMS[config.type][config.language]
}

export class AppAdGridBlockConfig {
  androidAID: string
  blockCacheId: string
  cacheTimeHours: number
  count: number
  countryCode: string
  countryCodeFromPlatform: boolean
  fallbackBehavior: string
  isDynamic: boolean
  language: TAppAdGridLanguage
  partner: string
  siteId: number
  type: TBlockAppAdGridSettingsType

  constructor(block: HTMLElement, language: TAppAdGridLanguage) {
    this.androidAID = androidAID()
    this.blockCacheId = block.dataset.mpAdGridBlockCachePerBlock === 'true' ? block.id : 'globalCache'
    this.cacheTimeHours = parseFloat(block.dataset.mpAdGridBlockCacheTimeHours)
    this.count = parseInt(block.dataset.mpAdGridBlockCount)
    this.countryCode = block.dataset.mpAdGridBlockCountryCode
    this.countryCodeFromPlatform = block.dataset.mpAdGridBlockCountryCodeFromPlatform === 'true'
    this.fallbackBehavior =
      block.dataset.mpAdGridBlockFallbackBehavior ||
      (TBlockAppAdGridSettingsFallbackBehavior.collapse as TBlockAppAdGridSettingsFallbackBehavior)
    this.isDynamic = block.dataset.mpAdGridBlockType === 'dynamic'
    this.language = language
    this.partner = block.dataset.mpAdGridBlockPartner
    this.siteId = parseInt(block.dataset.mpAdGridBlockSiteId)
    this.type = block.dataset.mpAdGridBlockType as TBlockAppAdGridSettingsType
  }
}

const initAdGridBlockSingle = async (block: HTMLElement, config: AppAdGridBlockConfig) => {
  const items = await getItems(config)
  const shuffled = config.isDynamic ? items : shuffle(items)
  const elements = Array.from(block.querySelectorAll('.block-item'))

  GA4HandlerLocal.blockRendered(block, config)

  neptuneDebug('initAdGridBlockSingle: type: ', config.type)
  neptuneDebug('initAdGridBlockSingle: isDynamic: ', config.isDynamic)
  neptuneDebug('initAdGridBlockSingle: count: ', config.count)
  neptuneDebug('initAdGridBlockSingle: items: ', items)
  neptuneDebug('initAdGridBlockSingle: shuffled: ', shuffled)

  fillAppAdElements(elements, shuffled, config.type)
}

class GA4HandlerLocal {
  static generateOptional(config: AppAdGridBlockConfig, optional: DataLayerOptionalEvent) {
    const root = document.querySelector('html')
    return {
      element_action: `app_ad_grid_block_render`,
      event: `app_ad_grid_block_render`,
      neptune_platform: root.getAttribute('data-gtm-partner'),
      neptune_product: root.getAttribute('data-gtm-product'),
      dt_app_media_partner: config.partner,
      dt_app_media_site_id: config.siteId,
      mode: isTwaMode() ? 'twa' : 'webview',
      ...optional
    }
  }

  static androidAIDMissing(block: HTMLElement, config: AppAdGridBlockConfig) {
    const detail = 'app_ad_grid_block_aaid_missing'
    const optional = this.generateOptional(config, { element_detail: detail })
    const event = GA4Handler.createGenericEvent<DataLayerAppAdGridEvent>(block, optional)

    GA4Handler.pushObject('GA4HandlerLocal.androidAIDMissing', event)
  }

  static blockRendered(block: HTMLElement, config: AppAdGridBlockConfig) {
    const detail = 'app_ad_grid_block_rendered'
    const optional = this.generateOptional(config, { element_detail: detail })
    const event = GA4Handler.createGenericEvent<DataLayerAppAdGridEvent>(block, optional)

    GA4Handler.pushObject('GA4HandlerLocal.blockRendered', event)
  }

  static doNotTrackEnabled(block: HTMLElement, config: AppAdGridBlockConfig) {
    const detail = 'app_ad_grid_dnt_enabled'
    const optional = this.generateOptional(config, { element_detail: detail })
    const event = GA4Handler.createGenericEvent<DataLayerAppAdGridEvent>(block, optional)

    GA4Handler.pushObject('GA4HandlerLocal.doNotTrackEnabled', event)
  }
}

/**
 * Initializes all app ad blocks on the page.
 *
 * @param lazy boolean Needed for unit testing.
 */
const initAdGridBlocks = async (lazy = true) => {
  try {
    const language = document.documentElement.lang === 'en' ? 'en' : 'en'
    const blocks: HTMLElement[] = Array.from(document.querySelectorAll(`[data-mp-ad-grid-block-type]`))

    // Loop through and initialize each block.
    for (const [index, block] of blocks.entries()) {
      try {
        // Setup impression handling at block level.
        GA4Handler.impressionHandleEvent(block)

        const config = new AppAdGridBlockConfig(block, language)

        // Collapsing when androidAID is missing is handled here to prevent block shifting.
        if (!config.androidAID) {
          block.style.display = 'none'
          if (doNotTrack()) {
            GA4HandlerLocal.doNotTrackEnabled(block, config)
            neptuneWarn('initAdGridBlocks: doNotTrack() is true, collapsing block')
          } else {
            GA4HandlerLocal.androidAIDMissing(block, config)
            neptuneWarn('initAdGridBlocks: androidAID missing, collapsing block')
          }
          continue
        }

        if (lazy) {
          lazyload(
            block,
            () => {
              initAdGridBlockSingle(block, config).catch(e => {
                block.style.display = 'none'
              })
            },
            {
              rootMargin: '0px',
              threshold: 0.25
            }
          )
        } else {
          // noinspection ES6MissingAwait
          initAdGridBlockSingle(block, config)
        }

        // Dev Testing: auto generated block IDs change on every page load in the dev server,
        // this allows testing of per-block caching locally.
        if (window.mp_globals.env.environment === 'development') {
          block.addEventListener('dblclick', () => {
            neptuneDebug(`handling app ads block ${block.id} refresh event`)
            initAdGridBlockSingle(block, config)
          })
        }
      } catch (e) {
        block.style.display = 'none'
      }
    }
  } catch (e) {
    console.log('initAdGridBlocks failed')
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // noinspection JSIgnoredPromiseFromCall
  initAdGridBlocks()
})

export default initAdGridBlocks
